import { Text, Icon, Avatar } from "@oysterhr/ui"
import LinkButton from "~/app/components/LinkButton"
import { countryCodeToName } from "@oysterhr/common"
import { useTranslation } from "react-i18next"
import { IconWithTooltip } from "@oysterhr/feature-components"
import { getBenefitPlanPrice, getBenefitsPlanName } from "../../../../utils/benefits"
import { BenefitsTier, CountryBenefits } from "@oysterhr/schema"
import { useTransformZendeskUrl } from "~/hooks/useTransformZendeskUrl"
import { EquityHiringAdvisor } from "@oysterhr/equity"

interface BenefitsHiringAdvisorProps {
  countryCode: string
  salaryCurrencyCode: string
  tier?: BenefitsTier
  countryBenefits: CountryBenefits[]
}

export const BenefitsHiringAdvisor = ({
  countryCode,
  tier,
  countryBenefits,
  salaryCurrencyCode,
}: BenefitsHiringAdvisorProps) => {
  const [t] = useTranslation("company_interface/CompanyNewHireFTEPage")

  return (
    <>
      {countryBenefits.length > 0 && (
        <>
          <Text className="mb-5 font-semibold">
            {t("benefitsStep.hiringAdvisor.information", {
              country: countryCodeToName(countryCode),
            })}
          </Text>
          <Text className="mb-2">
            {t("benefitsStep.hiringAdvisor.statutory", { country: countryCodeToName(countryCode) })}
          </Text>
          <ul className="mb-8 ml-5 list-disc">
            {countryBenefits.map(({ benefit, comment }) => (
              <li key={benefit}>
                <div className="flex flex-row items-center">
                  <span>{benefit}</span>
                  {comment && (
                    <span className="ml-4">
                      <IconWithTooltip value={comment} />
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
      {tier && (
        <>
          <Text className="mb-5 font-semibold">
            {t("benefitsStep.hiringAdvisor.includedPlans", {
              package: tier.name,
            })}
          </Text>
          <div className="space-y-3.5">
            {tier.includedPlans.map(plan => (
              <div
                key={plan.id}
                data-testid={`included-plan-${plan.id}`}
                className="flex flex-row space-x-3"
              >
                <div>
                  <Avatar name={getBenefitsPlanName(plan)} size="sm" src={plan.logoUrl} />
                </div>
                <div>
                  <div>
                    {plan.guideUrl ? (
                      <LinkButton
                        href={plan.guideUrl}
                        className="!ml-0 font-medium text-primary-base underline"
                        dataAnalyticsName="new-hire-form-benefits-step_benefit-plan-provider-link"
                        newTab
                      >
                        {getBenefitsPlanName(plan)}
                      </LinkButton>
                    ) : (
                      <span className="font-medium">{getBenefitsPlanName(plan)}</span>
                    )}
                    <span> {plan.descriptiveName}</span>
                  </div>
                  <div className="text-sm">
                    {plan.coverageTypes.length > 0 && (
                      <>
                        <span>{t("benefitsStep.hiringAdvisor.covers")} </span>
                        {plan.coverageTypes.map((coverage, i) => (
                          <span key={coverage}>
                            {t(coverage, { ns: "benefits/CoverageType" })}
                            {i + 1 === plan.coverageTypes.length ? ". " : ", "}
                          </span>
                        ))}
                      </>
                    )}
                    <span className="font-semibold">
                      {plan.priceEstimate
                        ? getBenefitPlanPrice(plan.priceEstimate, salaryCurrencyCode)
                        : t("benefitsStep.priceUnavailable")}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="mt-8">
        <EquityHiringAdvisor countryCode={countryCode} />
      </div>

      <div className="ml-2 mt-12 flex justify-end">
        <Icon size="sm" name="Question" data-testid="request-help-icon" />
        <LinkButton
          href={useTransformZendeskUrl(
            "https://support.oysterhr.com/hc/en-us/requests/new?ticket_form_id=6393889234193",
          )}
          className="!ml-1 font-medium text-primary-base underline"
          newTab
          testid="request-help-link-button"
          dataAnalyticsName="new-hire-form-benefits-step_request-help"
        >
          {t("benefitsStep.hiringAdvisor.requestHelp")}
        </LinkButton>
      </div>
    </>
  )
}
